import { ReactNode, useMemo } from 'react';

import { File05Icon } from '@/components/icons/File05Icon';
import { Home03Icon } from '@/components/icons/Home03Icon';
import { PresentationChart01Icon } from '@/components/icons/PresentationChart01Icon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import {
  SegmentProps,
  StepTracker,
  StepTrackerVariant,
} from '@/components/navigation/StepTracker/StepTracker';

import {
  getStepStatus,
  SMART_REVIEW_STEP_LABELS,
  SmartReviewStep,
} from '../SmartReviewOnboardingModal.types';
import { useDocumentUploadStepContext } from '../steps/documentUpload/context/documentUploadStep.context';
import { usePersistUploadedDocuments } from '../steps/documentUpload/hooks/usePersistUploadedDocuments';

/** Step-tracker icon for each step. */
const STEP_ICONS: Record<SmartReviewStep, ReactNode> = {
  [SmartReviewStep.ClientSetup]: <Home03Icon size={16} />,
  [SmartReviewStep.DocumentUpload]: <File05Icon size={16} />,
  [SmartReviewStep.SmartReviewOption]: <Stars01Icon size={16} />,
  [SmartReviewStep.Presentation]: <PresentationChart01Icon size={16} />,
};

interface SmartReviewOnboardingStepTrackerProps {
  currentStep: SmartReviewStep;
  stepOrder: SmartReviewStep[];
  /** Index of the furthest step the user reached in this session. */
  furthestStepIndex: number;
  /** Prevent all step navigation, e.g. while onboarding completes. */
  disabled: boolean;
  onNavigate: (step: SmartReviewStep) => void | Promise<void>;
}

/**
 * Step tracker for the SmartReview onboarding flow. After the household is
 * created, the user can click a step to move between Documents, SmartReview,
 * and Presentation. The user can go to a step only if they reached it before.
 * Basic info is never clickable, because the household already exists.
 */
export function SmartReviewOnboardingStepTracker({
  currentStep,
  stepOrder,
  furthestStepIndex,
  disabled,
  onNavigate,
}: SmartReviewOnboardingStepTrackerProps) {
  const { hasBeenClosed, markCommitted } = useDocumentUploadStepContext();
  const { persist } = usePersistUploadedDocuments();
  const activeIndex = stepOrder.indexOf(currentStep);
  // Basic info must submit through its own Continue.
  const isNavigationDisabled =
    disabled || currentStep === SmartReviewStep.ClientSetup;

  const segments = useMemo<SegmentProps[]>(() => {
    const handleNavigate = (step: SmartReviewStep) => {
      if (currentStep === SmartReviewStep.DocumentUpload) {
        if (hasBeenClosed()) return;
        // Leaving Documents commits the uploads, the same as its Continue.
        markCommitted();
        void persist();
      }
      void onNavigate(step);
    };

    return stepOrder.map((step, idx) => {
      const isNavigable =
        step !== SmartReviewStep.ClientSetup &&
        idx !== activeIndex &&
        idx <= furthestStepIndex;

      return {
        display: SMART_REVIEW_STEP_LABELS[step],
        status: getStepStatus(idx, activeIndex),
        icon: STEP_ICONS[step],
        onClick: isNavigable ? () => handleNavigate(step) : undefined,
      };
    });
  }, [
    activeIndex,
    currentStep,
    furthestStepIndex,
    hasBeenClosed,
    markCommitted,
    onNavigate,
    persist,
    stepOrder,
  ]);

  return (
    <StepTracker
      variant={StepTrackerVariant.Large}
      segments={segments}
      activeSegmentIndex={activeIndex}
      disableNavigation={isNavigationDisabled}
    />
  );
}
